// Standard Library

// 3rd Party

// Local

/**
 * LubooAuth - Automatic silent token refresh for authenticated sessions
 *
 * Security principles:
 * - NO token storage in JavaScript (tokens only in httpOnly cookies)
 * - Only reads token_expires_at cookie for timer calculation
 * - Server manages all cookies via Set-Cookie headers
 * - Automatic refresh ~1 minute before access token expiry
 */
class LubooAuth {
  /**
   * Constructor for LubooAuth.
   * Initializes the authentication module and schedules refresh timer.
   */
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this._currentUser = null; // Cached user data
    this.initialized = this._init();
  }

  /**
   * Initialize authentication module
   * @returns {Promise<boolean>} - True when initialized
   * @private
   */
  async _init() {
    try {
      // Schedule refresh if user is authenticated
      this.scheduleRefresh();

      // Set up global fetch interceptor for 401 handling
      this._setupFetchInterceptor();

      return true;
    } catch (error) {
      console.error('LubooAuth initialization error:', error);
      return false;
    }
  }

  /**
   * Get cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} - Cookie value or null
   * @private
   */
  _getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  /**
   * Get current user data
   *
   * SSR-first approach:
   * 1. Check window.LUBOO_CURRENT_USER (SSR-injected, zero latency)
   * 2. Fall back to /api/users/me if not available
   * 3. Cache result for subsequent calls
   *
   * Note: We don't check cookies here because access_token is httpOnly.
   * Just try to get user data and let 401 responses indicate not authenticated.
   * @returns {Promise<object | null>} - Current user object or null if not authenticated
   * @public
   */
  get currentUser() {
    return new Promise((resolve, reject) => {
      // Return cached user if available
      if (this._currentUser) {
        resolve(this._currentUser);
        return;
      }

      // Try SSR-injected user first (zero latency)
      if (window.LUBOO_CURRENT_USER) {
        this._currentUser = window.LUBOO_CURRENT_USER;
        resolve(this._currentUser);
        return;
      }

      // Fall back to API fetch (will return 401 if not authenticated)
      console.log('[LubooAuth] No SSR user data, fetching from API...');
      fetch('/api/users/me', {
        credentials: 'include', // Send httpOnly cookies
      })
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              // Not authenticated
              resolve(null);
              return;
            }
            throw new Error(`Failed to fetch user: ${response.status}`);
          }
          return response.json();
        })
        .then((userData) => {
          this._currentUser = userData;
          resolve(this._currentUser);
        })
        .catch((error) => {
          console.error('[LubooAuth] Failed to fetch current user:', error);
          reject(error);
        });
    });
  }

  /**
   * Schedule next token refresh
   *
   * Reads token_expires_at cookie and calculates time until refresh.
   * Refreshes ~1 minute before expiry to ensure seamless auth.
   * @public
   */
  scheduleRefresh() {
    // Clear existing timer
    this.clearTimer();

    // Check SSR-provided auth state first - if server says no user, don't try to refresh
    // This prevents redirect loops on public pages with stale cookies
    if (!window.LUBOO_CURRENT_USER) {
      console.log('[LubooAuth] No authenticated user (SSR), skipping refresh scheduling');
      return;
    }

    // Read expiry timestamp from cookie
    const expiresAtCookie = this._getCookie('token_expires_at');
    if (!expiresAtCookie) {
      // No session to schedule refresh for
      console.log('[LubooAuth] No session to schedule refresh for');
      return;
    }

    // Parse expiry (Unix timestamp in seconds)
    const expiryTime = parseInt(expiresAtCookie) * 1000; // Convert to milliseconds
    if (isNaN(expiryTime)) {
      console.error('[LubooAuth] Invalid token_expires_at cookie:', expiresAtCookie);
      return;
    }

    // Calculate refresh time (configurable buffer before expiry)
    const REFRESH_BUFFER_MS = window.LUBOO_CONFIG?.REFRESH_BUFFER_MS || 60000; // Read from server config with fallback
    const refreshTime = expiryTime - REFRESH_BUFFER_MS;
    const now = Date.now();
    const timeUntilRefresh = refreshTime - now;

    // If token already expired or expires very soon, refresh immediately
    if (timeUntilRefresh < 1000) {
      console.log('[LubooAuth] Token expired or expiring soon, refreshing immediately');
      this.refresh();
      return;
    }

    // Schedule refresh
    const refreshInSeconds = Math.round(timeUntilRefresh / 1000);
    console.log(`[LubooAuth] Scheduling refresh in ${refreshInSeconds} seconds`);

    this.refreshTimer = setTimeout(() => {
      this.refresh();
    }, timeUntilRefresh);
  }

  /**
   * Refresh access token using refresh token cookie
   *
   * Calls /api/auth/refresh with credentials (cookies sent automatically).
   * Server responds with new access_token and updates cookies via Set-Cookie headers.
   * @returns {Promise<boolean>} - True if refresh succeeded
   * @public
   */
  async refresh() {
    // Prevent concurrent refresh calls
    if (this.isRefreshing) {
      console.log('[LubooAuth] Refresh already in progress, skipping');
      return false;
    }

    this.isRefreshing = true;
    console.log('[LubooAuth] Refreshing access token...');

    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include', // Send cookies automatically
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Success! Server set new cookies via Set-Cookie headers
        await response.json();
        console.log('[LubooAuth] Token refreshed successfully');

        // Schedule next refresh based on new expiry
        this.scheduleRefresh();

        this.isRefreshing = false;
        return true;
      } else if (response.status === 401) {
        // Refresh token expired/invalid - redirect to login
        console.warn('[LubooAuth] Refresh token expired, redirecting to login');
        this.clearTimer();
        this.isRefreshing = false;
        window.location.href = '/login';
        return false;
      } else {
        // Other error - log and don't redirect (let timer retry)
        console.error('[LubooAuth] Refresh failed with status:', response.status);
        this.isRefreshing = false;
        return false;
      }
    } catch (error) {
      // Network error - log and don't redirect (let timer retry)
      console.error('[LubooAuth] Refresh error:', error);
      this.isRefreshing = false;
      return false;
    }
  }

  /**
   * Clear refresh timer
   * @public
   */
  clearTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Set up global fetch interceptor for automatic 401 handling
   *
   * Wraps native fetch to automatically retry requests after refreshing
   * tokens on 401 Unauthorized responses.
   * @private
   */
  _setupFetchInterceptor() {
    // Store original fetch
    const originalFetch = window.fetch;
    const self = this;

    // Override fetch with interceptor
    window.fetch = async function (...args) {
      // Always include credentials for same-origin requests
      const [url, options = {}] = args;
      if (!options.credentials) {
        options.credentials = 'include';
      }

      // Add CSRF token header for mutating requests
      const method = (options.method || 'GET').toUpperCase();
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        const cookieName = window.LUBOO_CONFIG?.CSRF_COOKIE_NAME || 'csrf_token';
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith(cookieName + '='))
          ?.split('=')[1];
        if (csrfToken) {
          if (!options.headers) options.headers = {};
          if (options.headers instanceof Headers) {
            options.headers.set('X-CSRF-Token', csrfToken);
          } else {
            options.headers['X-CSRF-Token'] = csrfToken;
          }
        }
      }

      // Make initial request
      let response = await originalFetch(url, options);

      // If 401 and not already a refresh request, try to refresh
      const urlStr = typeof url === 'string' ? url : url.toString();
      if (response.status === 401 && !urlStr.includes('/api/auth/refresh')) {
        console.log('[LubooAuth] Got 401, attempting token refresh...');

        // Try to refresh token
        const refreshed = await self.refresh();

        if (refreshed) {
          // Retry original request with new token
          console.log('[LubooAuth] Retrying request after refresh...');
          response = await originalFetch(url, options);
        } else {
          // Refresh failed, user will be redirected to login
          console.warn('[LubooAuth] Refresh failed, redirect to login pending');
        }
      }

      return response;
    };
  }
}

export { LubooAuth };
